import { useSyncExternalStore } from 'react'

export interface ViewportSnapshot {
  width: number
  height: number
  screenX: number
  screenY: number
}

const getViewportSnapshot = (): ViewportSnapshot => ({
  width: typeof window === 'undefined' ? 0 : window.innerWidth,
  height: typeof window === 'undefined' ? 0 : window.innerHeight,
  screenX: typeof window === 'undefined' ? 0 : window.screenX,
  screenY: typeof window === 'undefined' ? 0 : window.screenY,
})

const areViewportSnapshotsEqual = (a: ViewportSnapshot, b: ViewportSnapshot) =>
  a.width === b.width && a.height === b.height && a.screenX === b.screenX && a.screenY === b.screenY

// One shared store for all subscribers: a single resize listener + poll
// interval no matter how many components consume the snapshot, torn down when
// the last subscriber unmounts. Snapshot identity only changes when a value
// actually changed, so subscribers don't re-render in the steady state.
let snapshot = getViewportSnapshot()
let pollInterval: number | undefined
const listeners = new Set<() => void>()

const handleViewportChange = () => {
  const next = getViewportSnapshot()
  if (areViewportSnapshotsEqual(snapshot, next)) {
    return
  }
  snapshot = next
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  if (listeners.size === 0) {
    handleViewportChange()
    window.addEventListener('resize', handleViewportChange)
    pollInterval = window.setInterval(handleViewportChange, 250)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (listeners.size === 0) {
      window.removeEventListener('resize', handleViewportChange)
      window.clearInterval(pollInterval)
    }
  }
}

// While no subscriber (and thus no poll) is active the cache can go stale, and
// the first render happens before subscribe attaches in an effect — so refresh
// inline in that window. Identity is preserved unless a value actually changed,
// keeping renders in the same pass consistent.
const getSnapshot = () => {
  if (listeners.size === 0) {
    handleViewportChange()
  }
  return snapshot
}

/**
 * Live window size + screen position. Polls in addition to `resize` because
 * screenX/screenY changes (window moved across displays) fire no DOM event.
 */
export function useViewportSnapshot(): ViewportSnapshot {
  return useSyncExternalStore(subscribe, getSnapshot)
}
